import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { ArrowRight, Check } from 'lucide-react';
import type { KidWithDetails } from '../../../stores/kidStore';
import {
  seCoche,
  type ClasseDuCentre,
  type EcoleDesJournees,
  type EnfantAuChoix,
} from '../../../lib/jpa/regles';
import RangerDansUneClasse from './RangerDansUneClasse';

/*
  Étape 2 : un enfant, une carte, et la carte entière est la case — un
  toucher coche, un autre décoche. Rien n'est coché d'office.

  La couleur dit d'un coup d'œil où en est l'enfant face à l'école : vert, il
  peut participer ; orange, il manque sa classe dans cette école ou sa fiche,
  et on le demande une fois l'enfant coché ; gris, les journées ne sont pas
  ouvertes à sa classe. Aucune coche ne sert à autre chose qu'au choix du
  parent : la coche verte d'avant, qui disait « peut participer », était
  prise pour une case à décocher.
*/

const TONS = {
  vert: {
    anneau: 'ring-green-500',
    initiale: 'bg-green-50 text-green-800',
    pastille: 'bg-green-50 text-green-800 ring-green-300',
    point: 'bg-green-500',
  },
  orange: {
    anneau: 'ring-orange-400',
    initiale: 'bg-orange-50 text-orange-800',
    pastille: 'bg-orange-50 text-orange-800 ring-orange-300',
    point: 'bg-orange-500',
  },
  gris: {
    anneau: 'ring-gray-300',
    initiale: 'bg-gray-100 text-gray-500',
    pastille: 'bg-gray-100 text-gray-700 ring-gray-300',
    point: 'bg-gray-400',
  },
} as const;

const tonDe = (e: EnfantAuChoix): keyof typeof TONS =>
  e.cle === 'peut' || e.cle === 'deja' ? 'vert' : e.cle === 'fermee' ? 'gris' : 'orange';

function libelleDe(e: EnfantAuChoix): string {
  switch (e.cle) {
    case 'peut': return 'Peut participer';
    case 'deja': return 'Déjà inscrit·e';
    case 'classe': return e.pourquoi === 'autre_ecole' ? 'Pas encore dans cette école' : 'Classe à indiquer';
    case 'fiche': return 'Fiche à compléter';
    case 'fermee': return 'Pas ouverte à sa classe';
  }
}

interface Props {
  enfant: KidWithDetails;
  auChoix: EnfantAuChoix;
  /** Coché par le parent ; un enfant sans case (gris, déjà inscrit) ne l'est jamais à l'écran. */
  coche: boolean;
  onBasculer: () => void;
  ecole: EcoleDesJournees;
  annee: number;
  classesDuCentre: ClasseDuCentre[];
  /** La vérification de sa fiche, qui ramène ici. */
  lienDeLaFiche: string;
  onRange: () => Promise<void>;
}

export default function EnfantACocher({
  enfant, auChoix, coche: cochePar, onBasculer, ecole, annee, classesDuCentre, lienDeLaFiche, onRange,
}: Props) {
  const [changement, setChangement] = useState(false);
  const ton = TONS[tonDe(auChoix)];
  const cochable = seCoche(auChoix);
  /* Coché puis passé au gris (sa classe changée pour une classe non admise) : plus coché. */
  const coche = cochable && cochePar;
  const photo = enfant.photo_signed_url || enfant.photo_url;
  const classe = auChoix.cle === 'peut' || auChoix.cle === 'fermee' || auChoix.cle === 'deja' ? auChoix.classe : null;

  const ranger = (question: string, classeActuelle: ClasseDuCentre | null, onAnnuler?: () => void) =>
    ecole.ecoleId ? (
      <RangerDansUneClasse
        enfant={enfant}
        ecoleId={ecole.ecoleId}
        annee={annee}
        classes={classesDuCentre}
        classeActuelle={classeActuelle}
        question={question}
        onFait={async () => {
          await onRange();
          setChangement(false);
        }}
        onAnnuler={onAnnuler}
      />
    ) : (
      <p className="text-sm text-gray-700">
        Les classes de cette école ne sont pas encore en ligne : écrivez-nous à{' '}
        <a href="mailto:info@eclosion.be" className="font-semibold text-primary-700 underline">info@eclosion.be</a>.
      </p>
    );

  const identite = (
    <>
      <span
        className={clsx(
          'h-12 w-12 flex-shrink-0 overflow-hidden rounded-full ring-[3px] ring-offset-2',
          ton.anneau,
          coche ? 'ring-offset-primary-50' : cochable ? 'ring-offset-white' : 'ring-offset-gray-50',
        )}
        aria-hidden="true"
      >
        {photo ? (
          <img src={photo} alt="" className="h-full w-full object-cover" />
        ) : (
          <span className={clsx('flex h-full w-full items-center justify-center text-lg font-extrabold', ton.initiale)}>
            {enfant.prenom?.[0]?.toUpperCase()}
          </span>
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className={clsx('block truncate text-base font-extrabold', auChoix.cle === 'fermee' && 'text-gray-600')}>
          {enfant.prenom} {enfant.nom}
        </span>
        <span className={clsx('mt-1 inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-bold ring-1 ring-inset', ton.pastille)}>
          <span className={clsx('h-2 w-2 flex-shrink-0 rounded-full', ton.point)} aria-hidden="true" />
          {libelleDe(auChoix)}
        </span>
      </span>
    </>
  );

  /* Sous la carte : sa classe une fois coché, ou ce qui lui manque. */
  const classeEtChanger = classe && (
    <>
      <div className={clsx('flex items-center justify-between gap-2 border-t px-3 text-sm', coche ? 'border-primary-200' : 'border-gray-200')}>
        <span className="py-2 text-gray-700">
          Classe : <strong className="font-semibold">{classe.libelle}</strong>
        </span>
        {!changement && (
          <button type="button" onClick={() => setChangement(true)} className="min-h-[44px] px-1 font-semibold text-primary-700">
            Changer
          </button>
        )}
      </div>
      {changement && (
        <div className="px-3 pb-3">
          {ranger(`La classe de ${enfant.prenom}`, classe, () => setChangement(false))}
        </div>
      )}
    </>
  );

  let dessous: ReactNode = null;
  if (auChoix.cle === 'fermee') dessous = classeEtChanger;
  else if (coche && auChoix.cle === 'peut') dessous = classeEtChanger;
  else if (coche && auChoix.cle === 'classe') {
    dessous = (
      <div className="space-y-1 border-t border-primary-200 px-3 pb-3 pt-2">
        {ranger(`Dans quelle classe est ${enfant.prenom} ?`, null)}
        <p className="text-xs text-gray-600">
          {auChoix.pourquoi === 'autre_ecole'
            ? `Nous ajoutons ${ecole.ecole} et sa classe à sa fiche.`
            : 'Nous l’ajoutons à sa fiche.'}
        </p>
      </div>
    );
  } else if (coche && auChoix.cle === 'fiche') {
    dessous = (
      <div className="space-y-2 border-t border-primary-200 px-3 pb-3 pt-2">
        <p className="text-sm text-gray-700">Sa fiche est à compléter avant de l’inscrire (environ une minute).</p>
        <Link to={lienDeLaFiche} className="btn-primary inline-flex min-h-[44px] items-center gap-2 px-4 text-sm">
          Compléter la fiche de {enfant.prenom}
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>
    );
  }

  return (
    <section
      className={clsx(
        'overflow-hidden rounded-2xl border-2 transition-colors',
        coche ? 'border-primary-600 bg-primary-50' : cochable ? 'border-gray-200 bg-white hover:border-primary-300' : 'border-gray-200 bg-gray-50',
      )}
    >
      {cochable ? (
        <label className="flex min-h-[76px] cursor-pointer items-center gap-3 p-3">
          <input type="checkbox" checked={coche} onChange={onBasculer} className="peer sr-only" />
          <span
            className={clsx(
              'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg border-2',
              'peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500 peer-focus-visible:ring-offset-2',
              coche ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-400 bg-white',
            )}
            aria-hidden="true"
          >
            {coche && <Check className="h-5 w-5" strokeWidth={3} />}
          </span>
          {identite}
        </label>
      ) : (
        <div className="flex min-h-[76px] items-center gap-3 p-3">
          {/* Pas de case : la place reste, pour que les visages s'alignent. */}
          <span className="h-7 w-7 flex-shrink-0" aria-hidden="true" />
          {identite}
        </div>
      )}
      {dessous}
    </section>
  );
}
